import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useResponsiveValue } from '@/hooks/use-responsive-value';
import type { EntitySummary, Novel } from '@/lib/types';
import { CodexCard } from './codex-card';

interface CodexGridProps {
  entries: EntitySummary[] | undefined;
  activeTab: string;
  novel: Novel | null;
  onAllTabNewEntryClick: () => void;
}

export function CodexGrid({ entries, activeTab, novel, onAllTabNewEntryClick }: CodexGridProps) {
  const { t } = useTranslation();
  const resolveResponsive = useResponsiveValue();

  const getResponsiveSize = () => resolveResponsive({ xs: 6, sm: 4, lg: 3, xl: 2 });

  return (
    <Grid container spacing={3}>
      {/* Add Entry Card */}
      {activeTab === 'all' ? (
        <Grid size={getResponsiveSize()}>
          <Card
            onClick={onAllTabNewEntryClick}
            sx={{
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              bgcolor: 'transparent',
              textDecoration: 'none',
              transition: 'all 0.2s',
              borderRadius: 3,
              minHeight: 200,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <AddCircle sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="button" color="text.secondary">
              {t('entity_list.codex.new')}
            </Typography>
          </Card>
        </Grid>
      ) : (
        <Grid size={getResponsiveSize()}>
          <Link
            to="/codex/new"
            search={{ type: activeTab }}
            style={{ textDecoration: 'none' }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                bgcolor: 'transparent',
                textDecoration: 'none',
                transition: 'all 0.2s',
                borderRadius: 3,
                minHeight: 200,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <AddCircle sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="button" color="text.secondary">
                {t('entity_list.codex.new')}
              </Typography>
            </Card>
          </Link>
        </Grid>
      )}

      {novel && entries?.map((entry) => (
        <Grid key={entry.id} size={getResponsiveSize()}>
          <CodexCard
            entry={entry}
            categoryName={t(`entity_list.codex.categories.${entry.type}`, { defaultValue: novel?.codexCategories?.[entry.type] || entry.type })}
          />
        </Grid>
      ))}
    </Grid>
  );
}
